import { useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/features/auth/AuthContext";
import { useToast } from "@/ui/primitives/use-toast";
import { LGoButton, LGoFormField } from "@/ui/branded";
import { loginSchema, type LoginInput } from "@/domain/user/schema";

// GoTrue speaks English. Confirmations are required (ADR 0001), so "Email not
// confirmed" is now an everyday answer to a correct password, not an edge case.
const czechSignInErrors: Record<string, string> = {
  "Invalid login credentials": "Nesprávný email nebo heslo",
  "Email not confirmed":
    "E-mail zatím není potvrzený. Otevřete odkaz z e-mailu, který jsme poslali po registraci.",
};

interface LoginFormProps {
  onForgotPassword: () => void;
}

export function LoginForm({ onForgotPassword }: LoginFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  const onSubmit = async (data: LoginInput) => {
    setIsLoading(true);
    const { error } = await signIn(data.email, data.password);
    setIsLoading(false);

    if (error) {
      toast({
        variant: "destructive",
        title: "Chyba přihlášení",
        description: czechSignInErrors[error.message] ?? error.message,
      });
      return;
    }
    navigate("/");
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <LGoFormField
          name="email"
          label="Email"
          type="email"
          placeholder="vas@email.cz"
        />
        <LGoFormField name="password" label="Heslo">
          {({ value, onChange, onBlur, name, error }) => (
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={(value as string) ?? ""}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                name={name}
                className={`flex h-10 w-full rounded-md border bg-background px-3 pr-10 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${error ? "border-destructive" : "border-input"}`}
                disabled={isLoading}
                aria-invalid={!!error || undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          )}
        </LGoFormField>

        <div className="text-right">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
            disabled={isLoading}
          >
            Zapomněli jste heslo?
          </button>
        </div>

        <LGoButton type="submit" variant="primary" loading={isLoading} className="w-full">
          Přihlásit se
        </LGoButton>
      </form>
    </FormProvider>
  );
}
